"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ShieldCheck, Clock, FileCheck2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Action() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const lineVariants = {
    hidden: {
      scaleX: 0,
      opacity: 0
    },
    visible: {
      scaleX: 1,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 1, 0.5, 1]
      }
    }
  };
  const guarantees = [{
    title: "Confidencialidad absoluta",
    description: "Convenios de secrecía y resguardo seguro de información contable."
  }, {
    title: "Supervisión senior",
    description: "Revisión técnica guiada por socios con experiencia fiscal y legal."
  }, {
    title: "Respuesta oportuna",
    description: "Diagnóstico inicial y propuesta a la medida en menos de 48 horas."
  }];
  return <section id="llamado-a-la-acción" data-nav="dark" className="dark bg-background text-foreground relative overflow-hidden py-20 lg:py-28">
      {/* Subtle architectural border hairline top and bottom */}
      <div className="absolute inset-x-0 top-0 h-px bg-border/40" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-border/40" />

      {/* Decorative ambient gradients */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 size-96 rounded-full bg-accent/5 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.25
      }} className="mx-auto max-w-4xl text-center">
          {/* Eyebrow badge */}
          <motion.div variants={itemVariants} className="inline-flex items-center gap-2 mb-6">
            <span className="inline-block rounded-xs border border-accent/40 bg-accent/15 px-3 py-1 text-xs font-bold uppercase tracking-widest text-accent font-serif">
              Atención Empresarial e Institucional
            </span>
          </motion.div>

          {/* Display Heading */}
          <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl lg:text-5xl font-semibold uppercase tracking-wide font-serif text-foreground leading-tight">
            Optimice la estructura fiscal y contable de su empresa
          </motion.h2>

          {/* ASV Signature Gold Hairline */}
          <motion.div variants={lineVariants} style={{
          originX: 0.5
        }} className="mx-auto my-6 h-[2px] w-14 bg-accent" />

          {/* Supporting Copy */}
          <motion.p variants={itemVariants} className="text-base md:text-lg lg:text-xl text-muted-foreground font-sans max-w-2xl mx-auto leading-relaxed">
            Analizamos su situación financiera y tributaria con estricta confidencialidad y la
            supervisión directa de socios consultores.
          </motion.p>

          {/* Call to action button */}
          <motion.div variants={itemVariants} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="h-12 px-8 rounded-xs font-serif uppercase tracking-widest text-sm font-semibold border border-accent bg-accent text-foreground hover:bg-accent/90 hover:text-foreground shadow-sm transition-[color,background-color,border-color,box-shadow] duration-200 group">
              <Link to="/ContactPage">
                <span>Solicitar cotización</span>
                <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Button>
          </motion.div>

          {/* Institutional Trust Pillars */}
          <motion.div variants={itemVariants} className="mt-16 pt-12 border-t border-border/30 grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            {guarantees.map((item, index) => {
            const icons = [ShieldCheck, FileCheck2, Clock];
            const Icon = icons[index % icons.length];
            return <div key={item.title} data-index={index} className="flex items-start gap-3.5 p-4 rounded-xs bg-card/20 border border-border/30">
                  <div className="shrink-0 p-2 rounded-xs border border-accent/40 bg-accent/10 text-accent">
                    <Icon className="size-4" />
                  </div>
                  <div>
                    <h3 className="font-serif text-sm font-semibold uppercase tracking-wider text-foreground">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground font-sans leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>;
          })}
          </motion.div>
        </motion.div>
      </div>
    </section>;
}